import { CapitalMarketNewsItem } from "@/utils/types/news";
import { NewsItem } from "../types/news";

/**
 * Map API response to NewsItem format
 */
export const mapNewsToComponentFormat = (
  apiNews: CapitalMarketNewsItem
): NewsItem => {
  // Format date to relative time (e.g., "2h ago", "1d ago")
  // Uses date + time fields from API (assumes IST timezone)
  const formatTimeAgo = (
    dateStr: string,
    timeStr: string,
    createdOn?: string
  ): string => {
    try {
      let date: Date | null = null;

      // Prioritize date + time fields from API (not created_on)
      if (dateStr && timeStr) {
        try {
          // Parse date format like "1/13/2026 12:00:00 AM" and combine with time "16:43"
          // Since API provides IST time, we'll construct it assuming IST (+05:30)
          const datePart = dateStr.split(" ")[0]; // "1/13/2026"
          const [month, day, year] = datePart.split("/");
          const [hours, minutes] = timeStr.split(":");

          if (month && day && year && hours && minutes) {
            // Create date string in ISO format with IST timezone (+05:30)
            const isoString = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}T${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}:00+05:30`;
            date = new Date(isoString);

            // Validate the constructed date
            if (isNaN(date?.getTime() ?? NaN)) {
              console.warn(
                "Invalid constructed date from dateStr and timeStr:",
                dateStr,
                timeStr
              );
              date = null;
            }
          }
        } catch (parseError) {
          console.warn("Error parsing date/time:", parseError);
          date = null;
        }
      }

      // Fallback: If date + time is not available, use created_on
      if (!date && createdOn?.trim()) {
        // Parse ISO datetime string like "2026-01-13T17:58:17+0530"
        date = new Date(createdOn);

        // Validate the parsed date
        if (isNaN(date.getTime())) {
          console.warn("Invalid created_on date:", createdOn);
          date = null;
        }
      }

      // If we still don't have a valid date, return time string or default
      if (!date || isNaN(date.getTime())) {
        return timeStr || "N/A";
      }

      // Get current time (this will be in user's local timezone, but comparison is still correct
      // because both dates are converted to milliseconds since epoch)
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();

      // If date is in the future (shouldn't happen but handle it)
      if (diffMs < 0) {
        return timeStr || "N/A";
      }

      // Calculate time differences
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      // Format relative time
      if (diffSeconds < 60) {
        return "Just now";
      } else if (diffMinutes < 60) {
        return `${diffMinutes}m ago`;
      } else if (diffHours < 24) {
        return `${diffHours}h ago`;
      } else if (diffDays < 7) {
        return `${diffDays}d ago`;
      } else {
        const weeks = Math.floor(diffDays / 7);
        if (weeks < 4) {
          return `${weeks}w ago`;
        } else {
          const months = Math.floor(diffDays / 30);
          return months < 12
            ? `${months}mo ago`
            : `${Math.floor(diffDays / 365)}y ago`;
        }
      }
    } catch (error) {
      console.error("Error formatting time ago:", error, {
        dateStr,
        timeStr,
        createdOn,
      });
      return timeStr || "N/A";
    }
  };

  // Extract plain text from HTML and get first 1-2 lines
  const extractSummaryFromArttext = (arttext: string): string => {
    if (!arttext) {
      return "";
    }
    // Remove HTML tags
    const textContent = arttext.replace(/<[^>]*>/g, "");
    // Remove extra whitespace and newlines
    const cleanText = textContent.replace(/\s+/g, " ").trim();
    // Remove "Powered by Capital Market - Live News" if present
    const withoutPoweredBy = cleanText
      .replace(/Powered by.*Capital Market.*Live News/gi, "")
      .trim();
    return withoutPoweredBy;
  };

  // Calculate read time based on content length
  const calculateReadTime = (content: string): string => {
    const wordsPerMinute = 200;
    const textContent = content.replace(/<[^>]*>/g, ""); // Remove HTML tags
    const wordCount = textContent.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return `${minutes} min read`;
  };

  return {
    id: parseInt(apiNews.id) || parseInt(apiNews.sno) || 0,
    title: apiNews.heading || "Untitled",
    summary: extractSummaryFromArttext(apiNews.arttext || ""), // Extract plain text from arttext
    content: apiNews.arttext || "",
    category: apiNews.section_name || "General",
    time: formatTimeAgo(apiNews.date, apiNews.time, apiNews.created_on),
    caption: apiNews.caption || "", // Use caption directly as it's already formatted as "On 16 January 2026"
    source: "Capital Market News", // Keep as default since API doesn't provide this
    author: "Market Analyst", // Keep as default since API doesn't provide this
    readTime: calculateReadTime(apiNews.arttext || ""),
  };
};
